import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { toastService } from '../../services/toastService';
import StatsCard from '../../components/cards/StatsCard';
import ActivityChart from './ActivityChart';
import RecentSubmissions from './RecentSubmissions';
import ProjectPerformanceChart from './ProjectPerformanceChart';
import { PlusIcon } from '../../components/Icons/svgIcons';
import StartFormModal from '../../components/modals/StartFormModal';
import StatsCardSkeleton from '../../components/skeletons/StatsCardSkeleton';
import ChartSkeleton from '../../components/skeletons/ChartSkeleton';
import ListSkeleton from '../../components/skeletons/ListSkeleton';
import { useDashboard } from '../../hooks/useDashboard';
import MainPageLayout from '../../components/layout/MainPageLayout';
import { DashboardTour } from '../../components/ui/tour/DashboardTour';
import SyncTrustBanner from '../../components/common/SyncTrustBanner';

const Dashboard = () => {
  const location = useLocation();
  const {
    submissions,
    forms,
    stats,
    isModalOpen,
    setIsModalOpen,
    statsCardsConfig,
    isStatsLoading,
    isSubmissionsLoading,
    isFormsLoading,
    isUnauthorized
  } = useDashboard({
    onUnauthorizedClick: () => {
      toastService.error("You are not authorized to view submissions. Please contact the admin if you have any questions.");
    }
  });

  const hasSubmissions = submissions && submissions.length > 0;

  useEffect(() => {
    if (location.state?.unauthorizedAccess) {
      toastService.error("You are not authorized to view submissions. Please contact the admin if you have any questions.");
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  return (
    <div className="h-full relative bg-[var(--bg-body)]">
      <MainPageLayout>
        <div className="space-y-6">
          
          <SyncTrustBanner />

          <div className="flex flex-col gap-3 sm:gap-4">
            <div id="tour-stats-row-1" className="grid grid-cols-2 gap-3 sm:gap-4">
              {isStatsLoading && Object.keys(stats).length === 0 ? (
                Array.from({ length: 2 }).map((_, index) => <StatsCardSkeleton key={index} />)
              ) : (
                statsCardsConfig.slice(0, 2).map(stat => <StatsCard key={stat.id} {...stat} />)
              )}
            </div>
            <div id="tour-stats-row-2" className="grid grid-cols-2 gap-3 sm:gap-4">
              {isStatsLoading && Object.keys(stats).length === 0 ? (
                Array.from({ length: 2 }).map((_, index) => <StatsCardSkeleton key={`skeleton-2-${index}`} />)
              ) : (
                statsCardsConfig.slice(2, 4).map(stat => <StatsCard key={stat.id} {...stat} />)
              )}
            </div>
          </div>

          <div id="tour-activity-chart">
          {(isSubmissionsLoading && submissions.length === 0) || (isStatsLoading && Object.keys(stats).length === 0) ? (
            <div className="card-base p-1"><ChartSkeleton /></div>
          ) : (
            <ActivityChart status={(isSubmissionsLoading || isStatsLoading) ? 'loading' : 'succeeded'} />
          )}
          </div>
          
          {!(isFormsLoading && forms.length === 0) && forms && forms.length > 0 && <ProjectPerformanceChart forms={forms} />}

          <div id="tour-recent-submissions">
          {(isSubmissionsLoading && submissions.length === 0) ? (
            <div className="card-base p-5">
               <ListSkeleton items={3} showAvatar={false} showHeader={false} />
            </div>
          ) : (hasSubmissions && !isUnauthorized) ? (
            <RecentSubmissions submissions={submissions} status={isSubmissionsLoading ? 'loading' : 'succeeded'} />
          ) : null}
          </div>

        </div>
      </MainPageLayout>

      <button
        id="tour-fab-button"
        onClick={() => setIsModalOpen(true)}
        className="fixed bottom-20 right-4 md:absolute md:bottom-8 md:right-6 w-14 h-14 bg-[var(--btn-secondary-bg)] rounded-full flex items-center justify-center shadow-lg shadow-blue-900/20 transform transition-all hover:scale-105 active:scale-95 z-40 hover:shadow-xl border-2 border-white"
        aria-label="Start new survey"
      >
        <PlusIcon className="w-7 h-7 text-white" />
      </button>

      <StartFormModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
      <DashboardTour />
    </div>
  );
};

export default Dashboard;